<template>
  <div>

    <filter-targeted-content :filterStatus="filterStatus" @filter-item="filterWaves" />

    <div class="container mx-auto">

      <empty v-if="loaded && waves.length === 0" icon="water" title="No waves on the horizon"
        message="The weather is not optimal for surfing the waves.">
      </empty>

      <div v-else-if="loaded && waves.length > 0" class="-mx-4 sm:-mx-8 px-4 sm:px-8 py-4 overflow-x-auto">
        <div class="inline-block min-w-full shadow rounded-lg overflow-hidden">
          <table class="min-w-full leading-normal">
            <thead>
              <tr>
                <th
                  class="px-5 py-3 border-b-2 border-gray-200 bg-gray-100 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">
                  Name
                </th>
                <th
                  class="px-5 py-3 border-b-2 border-gray-200 bg-gray-100 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">
                  <el-tooltip effect="dark" content="To test the wave, send header X-Wave-Code: XXX and X-Lang: fr"
                    placement="top"><span class="cursor-pointer">Code (?)</span></el-tooltip>
                </th>
                <th
                  class="px-5 py-3 border-b-2 border-gray-200 bg-gray-100 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">
                  Status
                </th>
                <th
                  class="px-5 py-3 border-b-2 border-gray-200 bg-gray-100 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">
                  Who
                </th>
                <th
                  class="px-5 py-3 border-b-2 border-gray-200 bg-gray-100 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">
                  Where
                </th>
                <th
                  class="px-5 py-3 border-b-2 border-gray-200 bg-gray-100 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">
                  When
                </th>
                <th
                  class="px-5 py-3 border-b-2 border-gray-200 bg-gray-100 text-center text-xs font-semibold text-gray-600 uppercase tracking-wider">
                  <el-tooltip effect="dark" content="Unique Wave Views" placement="top"><span class="cursor-pointer">UWV
                      (?)</span></el-tooltip>
                </th>
                <th
                  class="px-5 py-3 border-b-2 border-gray-200 bg-gray-100 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">
                </th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="(wave, index) in waves" :key="wave.id">
                <td class="px-5 py-5 border-b border-gray-200 bg-white text-sm">
                  <p class="text-gray-900 whitespace-no-wrap">{{ wave.name }}</p>
                  <p class="text-xs">{{ new Date(wave.datecreation).toLocaleDateString('fr') }}</p>
                </td>

                <td class="px-5 py-5 border-b border-gray-200 bg-white text-sm">
                  <div class="fields-container">
                    <div class="relative">
                      <button class="rounded bg-gray-200 button-field font-semibold" v-on:click="(e) => {copyFieldToClipBoard(e, wave.code)}">
                        {{ wave.code }}
                      </button>
                      <div class="copy-popin">Copied !</div>
                    </div>
                  </div>
                </td>

                <td class="px-5 py-5 border-b border-gray-200 bg-white text-sm">
                  <span class="relative inline-block px-3 py-1 font-semibold leading-tight" :class="{
                    'text-green-900': wave.status === 'enable' || wave.status === 'pending' || wave.status === 'terminated',
                    'text-gray-900': wave.status === 'disable'
                  }">
                    <span aria-hidden class="absolute inset-0 opacity-50 rounded" :class="{
                      'bg-green-200': wave.status === 'enable' || wave.status === 'pending' || wave.status === 'terminated',
                      'bg-gray-200': wave.status === 'disable'
                    }"></span>
                    <span class="relative">
                      <template
                        v-if="wave.status === 'enable'">Enable</template>
                      <template
                        v-else-if="wave.status === 'pending'">Pending</template>
                      <template
                        v-else-if="wave.status === 'terminated'">Terminated</template>
                      <template v-else>Disable</template>
                    </span>
                  </span>
                </td>

                <td class="px-5 py-5 border-b border-gray-200 bg-white text-sm">
                  <p class="text-gray-900 whitespace-no-wrap">{{ wave.target_audience }}</p>
                </td>

                <td class="px-5 py-5 border-b border-gray-200 bg-white text-sm">
                  <p class="text-gray-900 whitespace-no-wrap">
                    <template v-if="wave.url_target_list.includes('.*')">Every where on <br>{{ wave.url }}</template>
                    <template v-else>
                      <span :key="index" class="relative inline-block px-3 py-1 text-gray-900 leading-tight">
                          <span aria-hidden class="absolute inset-0 bg-gray-200 opacity-50 rounded mr-2"></span>
                          <span class="relative">{{ wave.url_target_string }}</span>
                      </span>
                      on <br>{{ wave.url }}</template>
                  </p>
                </td>

                <td class="px-5 py-5 border-b border-gray-200 bg-white text-sm">
                  <p class="text-gray-900 whitespace-no-wrap">
                    <span v-if="wave.frequencylimitation === 'none' && wave.frequencylimitationvalue === 1">Show every
                      time</span>
                    <span v-if="wave.frequencylimitation === 'none' && wave.frequencylimitationvalue === 0">Only
                      manually</span>
                    <span v-if="wave.frequencylimitation !== 'none' && wave.frequencylimitationvalue > 0">Show {{
                        wave.frequencylimitationvalue
                    }} times per {{ wave.frequencylimitation }}</span>
                    <span v-if="wave.frequencylimitation !== 'none' && wave.frequencylimitationvalue === 0">Can't
                      show</span>

                    <span v-if="wave.activationdate && wave.deactivationdate">between {{ _getDate(wave.activationdate, site.timezone) | date }}
                      and {{ _getDate(wave.deactivationdate, site.timezone) | date }}</span>
                  </p>
                </td>

                <td class="px-5 py-5 border-b border-gray-200 bg-white text-center">
                  <p class="text-gray-900 whitespace-no-wrap">
                    <span v-for="(view) in viewsUnique" :key="view.id">
                      <span v-if="view.id === wave.id">
                        {{ view.views }}&nbsp;
                        <font-awesome-icon icon="eye" @click="openPopup(index)"></font-awesome-icon>
                        <waves-ui-modal-stats v-if="isOpenTable[index]" :open="isOpenTable[index]" :site="site"
                          :close="closeTable" :id="wave.id" />
                      </span>
                    </span>
                  </p>
                </td>

                <td class="px-5 py-5 border-b border-gray-200 bg-white text-sm text-right">
                  <router-link class="cursor-pointer text-gray-600 bg-gray-200 rounded p-2"
                    :to="{ name: 'Wave', params: { site: site.sitekey, id: wave.id } }">
                    <font-awesome-icon icon="pencil-alt"></font-awesome-icon>
                  </router-link>
                  <a class="cursor-pointer text-gray-600 bg-gray-200 rounded p-2" @click="duplicateWave(wave.id)">
                    <font-awesome-icon icon="clone"></font-awesome-icon>
                  </a>
                  <a @click="confirmDeletion(wave)" class="cursor-pointer text-gray-600 bg-gray-200 rounded p-2">
                    <font-awesome-icon icon="trash"></font-awesome-icon>
                  </a>
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>

      <div v-if="loaded && waves.length > 0" class="text-right px-2">
        <p v-if="waves.length > 1">{{ waves.length }} results</p>
        <p v-else>{{ waves.length }} result</p>
      </div>

      <modal-confirmation v-if="deleteWaveModalConfirmation" :open="deleteWaveModalConfirmation" type="danger"
        messageTitle="Warnings !" message="Do you really want to remove this wave ? This actions cannot be undone."
        messageAction="Yes, I'm sure !" @execute="deleteWave(waveToDelete)"
        @close='deleteWaveModalConfirmation = false' />
    </div>
  </div>

</template>

<script>
import Empty from '@/components/box-entity-empty.vue'
import ModalConfirmation from '@/components/others/utils/ModalConfirmation.vue'
import FilterTargetedContent from '@/components/common/ui/form/filter-targeted-content.vue'
import { mapActions, mapGetters } from 'vuex'

export default {
  name: 'WaveUiList',

  components: { Empty, ModalConfirmation, FilterTargetedContent },

  data() {
    return {
      viewsUnique: [],
      waveToDelete: null,
      loaded: false,
      deleteWaveModalConfirmation: null,
      isOpenTable: [],
      waves: [],
      filterStatus: 'all',
    }
  },
  watch: {
    site: {
      handler: 'init',
      immediate: true
    }
  },
  computed: {
    ...mapGetters('waves', {
      allWaves: 'waves',
      wavesByStatus: 'getByStatus',
      getFilterStatus: 'getFilterStatus'
    }),
    ...mapGetters('account', {
      site: 'currentSite',
      lang: 'defaultLang'
    })
  },
  methods: {
    ...mapActions('waves', ['loadWaves', 'getStepStats', 'removeWave', 'addWave', 'setFilterStatus']),

    copyFieldToClipBoard(e, field) {
      navigator.clipboard.writeText(field)
      e.target.classList.add("active");
      setTimeout(() => {
        e.target.classList.remove("active")
      }, 1000)
    },

    openPopup(index) {
      this.isOpenTable[index] = true
      this.$forceUpdate()
    },
    init() {
      this.loadWaves().then(() => {
        this.loaded = true
        this.filterStatus = this.getFilterStatus
        this.waves = this.wavesByStatus(this.filterStatus)
        this.getUniqueViews(this.waves)
        this.getTableBoolean(this.waves)
        this.getUrlTargetToString(this.waves)
      })
    },
    getUniqueViews(waves) {
      this.viewsUnique = [];

      for (var wave of waves) {
        if (wave.status !== 'enable' && 'views' in wave && wave.views !== null) {
          let uniqueViews = wave.views.uniqueViews ? wave.views.uniqueViews : 0;
          this.viewsUnique.push({
            id: wave.id,
            views: uniqueViews.toString()
          });
        } else {
          this.getStepStats({ id: wave.id }).then((data) => {
            let uniqueViews = data.results.uniqueViews ? data.results.uniqueViews : 0;
            this.viewsUnique.push({
              id: data.id,
              views: uniqueViews.toString()
            });
          });
        }
      }
    },
    getTableBoolean(waves) {
      for (let i = 0; i < waves.length; ++i) {
        this.isOpenTable[i] = false
      }
    },
    getUrlTargetToString(waves) {
      for (let wave of waves) {
        let urlString = ""
        wave.url_target_list.forEach((path, index) => {
          if (index === 0) {
            urlString += path
          } else {
            urlString += ', ' + path
          }
        })
        wave.url_target_string = urlString;
      }
    },

    duplicateWave(idWave) {
      let wave = this.waves.find(w => w.id === idWave)
      let waveClone = Object.assign({}, wave)

      waveClone.status = 'disable'
      waveClone.name = 'Copy of ' + waveClone.name
      if (waveClone.reward && waveClone.reward !== null && !waveClone.reward.length) waveClone.reward = null

      this.addWave({
        wave: waveClone
      })
        .then(() => {
          this.$notify({
            title: 'Success',
            message: this.$root.$t('notifications.wave.duplicated'),
            type: 'success'
          })
          let waveCreated = this.waves.pop();
          this.waves.splice(0, 0, waveCreated);
        })
        .catch((response) => {
          this.$notify.error({
            title: this.$root.$t('api.' + response.data.code + '.title'),
            message: this.$root.$t('api.' + response.data.code + '.message')
          })
        })
    },

    confirmDeletion(wave) {
      this.waveToDelete = wave
      this.deleteWaveModalConfirmation = true
    },

    deleteWave(wave) {
      this.removeWave({ wave: wave })
        .then(() => {
          this.$notify({
            title: 'Success',
            message: this.$root.$t('notifications.wave.deleted'),
            type: 'success'
          })
        })
        .catch((response) => {
          this.$notify.error({
            title: this.$root.$t('api.' + response.data.code + '.title'),
            message: this.$root.$t('api.' + response.data.code + '.message')
          })
        })
    },

    filterWaves (filterStatus) {
      this.filterStatus = filterStatus
      this.waves = this.wavesByStatus(filterStatus)
      this.setFilterStatus({filterStatus});
    },

    mounted() {
      this.init()
    },
    closeTable() {
      for (let i = 0; i < this.waves.length; ++i) {
        this.isOpenTable[i] = false
      }
      this.$forceUpdate()
    }
  }
}
</script>

<style>
  .fields-container{
    display: flex;
    gap: 10px 16px;
    flex-flow: row wrap;
  }

  .button-field {
    padding: 4px 8px;
  }

  .copy-popin {
    display: none;
    z-index: 10;
    position: absolute;
    top: -36px;
    left: 50%;
    width: 70px;
    box-sizing: border-box;
    margin-left: -35px;
    padding: 4px 8px;
    text-align: center;
    border-radius: 0.25rem;
    background-color: #f59e0b;
    color: #fff;
  }

  .button-field.active ~ .copy-popin {
    display: block;
    animation: 1s linear pop;
    transform: scale(0);
  }

  @keyframes pop {
    from { transform: scale(0.5); }
    10%   { transform: scale(1); }
    90%   { transform: scale(1); }
    to { transform: scale(0.5); }
  }
</style>



// WEBPACK FOOTER //
// src/components/waves/ui/list.vue